import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from 'underscore';

import * as Sentry from '@sentry/angular';

import {
  faCalendarDays,
  faPrint,
  faSquarePollHorizontal,
  faFileExcel,
  faSpinner,
  faCircleExclamation,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

import { sessionIntroSteps } from './session.intro-steps';

import { StudentsService } from '../../../../core/services/students.service';
import { ReportsService } from '../../../../core/services/reports.service';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import { ManagementUser } from '../../../../core/models/management-user.model';
import { District } from '../../../../core/models/district.model';
import { School } from '../../../../core/models/school.model';
import { SchoolClass } from '../../../../core/models/school-class.model';
import { SubscriptionTypes } from '../../../../core/models/subscription-types.model';
import { ALL_STUDENTS, SessionStudent, sessionStudentKey, StudentSessionSummary } from '../../../../core/models/student-session-summary.model';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UtilityService } from '../../../../core/services/utility.service';
import { PrintService } from 'src/app/core/services/print.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { canViewCoTeacherFilter, canViewSchoolFilter } from 'src/app/reports/utils';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

@Component({
    selector: 'wf-session-summary',
    templateUrl: './session.component.html',
    styleUrls: ['./session.component.css'],
    standalone: false
})
export class SessionSummaryComponent implements OnInit {
  currentUser: ManagementUser | null = null ;
  showSchoolFilter: boolean = false ;
  schoolFilterError: string = '' ;
  showCoTeacherFilter: boolean = false ;
  hideSharedStudents: boolean = false ;
  reportError: string = '' ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  grades: string[] = [] ;
  classes: SchoolClass[] = [] ;
  sessions: StudentSessionSummary[] = [] ;
  students: SessionStudent[] = [] ;
  selectedStudent: SessionStudent | undefined ;
  studentFilterOpts: any = null ;
  filteredSessions: StudentSessionSummary[] = [] ;
  filteredSessionsWithSeconds: (StudentSessionSummary & { seconds: number, dateRange: { startDate: string, endDate: string } })[] = [] ;
  startDate: NgbDateStruct = {
    year: (new Date()).getFullYear(),
    month: (new Date()).getMonth() + 1,
    day: (new Date()).getDate(),
  } ;
  endDate: NgbDateStruct = {
    year: (new Date()).getFullYear(),
    month: (new Date()).getMonth() + 1,
    day: (new Date()).getDate(),
  } ;
  sortColumn: string = '' ;
  sortReverse: boolean = false ;
  fullProduct: string = SubscriptionTypes.FullProduct ;
  exportingReport: boolean = false ;
  reportsIcon = CATEGORY_ICONS['class-reports'];
  printIcon = faPrint ;
  summaryIcon = faSquarePollHorizontal ;
  calendarIcon = faCalendarDays ;
  excelIcon = faFileExcel ;
  loadingIcon = faSpinner;
  errorIcon = faCircleExclamation ;
  helpIcon = faQuestionCircle;
  viewType: string = 'sessions' ;
  tourSteps = sessionIntroSteps;

  minDate: NgbDateStruct = {
    year: (new Date()).getFullYear() - 5,
    month: (new Date()).getMonth() + 1,
    day: (new Date()).getDate(),
  } ;

  maxDate: NgbDateStruct = {
    year: (new Date()).getFullYear() ,
    month: (new Date()).getMonth() + 1,
    day: (new Date()).getDate(),
  } ;

  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;

  constructor(
    private route: ActivatedRoute,
    private studentService: StudentsService,
    private reportService: ReportsService,
    private utilityService: UtilityService,
    private sessionStorageService: SessionStorageService,
    private printService: PrintService,
    private loadingService: LoadingService,
  ) { }

  ngOnInit(): void {
    let resolvedData = this.route.snapshot.data['resolveData'] ;
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    this.sessions = resolvedData.studentSessions ;
    this.filteredSessions = resolvedData.studentSessions ;
    this.buildStudentList() ;
    this.grades = resolvedData.grades ;
    this.classes = resolvedData.classes ;

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }

  }

  filterSessions(filterOpts: any) {
    this.studentFilterOpts = filterOpts ;
    this.applyFilters() ;
  }

  filterCoTeacherSessions(sessions: StudentSessionSummary[]) {
    return this.studentService.filterCoTeacherSessionSummary(sessions, this.currentUser!.userID, this.hideSharedStudents) ;
  }

  onFilterCoTeacherChange() {
    this.buildStudentList() ;
    this.applyFilters() ;
  }

  onStudentChange() {
    this.applyFilters() ;
  }

  // The student list is only meaningful once the sessions are scoped to a single school. District and FIL users viewing
  // 'All' schools (schoolID 0) would otherwise get an overwhelming list, so the filter stays disabled until they narrow down.
  get canSelectStudent(): boolean {
    return this.reportService.getSelectedSchoolForReports().schoolID !== 0 ;
  }

  buildStudentList() {
    if (!this.canSelectStudent) {
      this.students = [ ALL_STUDENTS ] ;
      this.selectedStudent = undefined ;
      return ;
    }

    // Build from the co-teacher-filtered set so hiding shared students drops them from the dropdown too, not just the table
    let uniqueStudents: SessionStudent[] = _.uniq(this.filterCoTeacherSessions(this.sessions), false, sessionStudentKey)
      .map((session: StudentSessionSummary) => ({
        key: sessionStudentKey(session),
        firstName: session.firstName,
        lastName: session.lastName,
      })) ;

    this.students = [ ALL_STUDENTS ].concat(_.sortBy(uniqueStudents, 'lastName')) ;

    // Hold onto the current selection if that student still has sessions, otherwise fall back to All Students
    this.selectedStudent = this.students.find((student) => student.key === this.selectedStudent?.key) || this.students[0] ;
  }

  applyFilters() {
    let sessions = this.filterCoTeacherSessions(this.studentService.filterStudentSummarySessions(this.sessions, this.studentFilterOpts)) ;

    if (this.selectedStudent && this.selectedStudent.key !== ALL_STUDENTS.key) {
      sessions = sessions.filter((session) => sessionStudentKey(session) === this.selectedStudent!.key) ;
    }
    this.filteredSessions = sessions ;

    if (this.viewType === 'minutes') {
      this.handleViewChange(undefined, 'minutes');
    }
  }

  updateStudentSessions() {
    let startDateObj = new Date(this.startDate.year, this.startDate.month -1, this.startDate.day, 0, 0, 1) ;
    let endDateObj = new Date(this.endDate.year, this.endDate.month - 1, this.endDate.day, 23, 59, 59) ;

    this.reportService.getSessionSummaryData([startDateObj.getTime(), endDateObj.getTime()]).subscribe({
      next: (data) => {
        if (data.erred) {
          this.schoolFilterError = data.message;
        } else {
          this.schoolFilterError = '';
          this.grades = data.grades;
          this.classes = data.classes;

          this.sessions = data.studentSessions;
          this.sessions = this.sessions.filter((session, index, self) =>
            index === self.findIndex((s) =>
              s.firstName === session.firstName &&
              s.lastName === session.lastName &&
              s.startDate === session.startDate &&
              s.endDate === session.endDate
            )
          );

          this.buildStudentList() ;
          this.applyFilters() ;
        }
      },
      error: (err: any) => {
        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-summary-session',
            startDate: startDateObj.toLocaleDateString('en-US'),
            endDate: endDateObj.toLocaleDateString('en-US'),
          }
        }) ;
      }
    })
  }

  refreshingTableData(){
    this.loadingService.start() ;
    this.viewType = 'sessions' ;
    let startDateObj = new Date(this.startDate.year, this.startDate.month -1, this.startDate.day, 0, 0, 1) ;
    let endDateObj = new Date(this.endDate.year, this.endDate.month - 1, this.endDate.day, 23, 59, 59) ;

    this.reportService.getSessionSummaryData([startDateObj.getTime(), endDateObj.getTime()]).subscribe({
      next: (data) => {
        if (data.erred) {
          this.schoolFilterError = data.message;
        } else {
          this.schoolFilterError = '';
          this.grades = data.grades;
          this.classes = data.classes;

          this.sessions = data.studentSessions;

          // FiXME: Next time we do impactful work on the session summary report, we should consider refactoring this. This loop
          //      : basically is de-duping any student sessions for the same student, that starts and ends at the same time. In
          //      : our system, this really shouldn't be possible so if we were seeing duplicated sessions here it might indicate
          //      : the report data coming back might be sending back more results that it should. Right now, this is not doing
          //      : anything impactful, besides maybe wasting a CPU cycle or two (MFS, BNI, 11/18/25)
          this.sessions = this.sessions.filter((session, index, self) =>
            index === self.findIndex((s) =>
              s.firstName === session.firstName &&
              s.lastName === session.lastName &&
              s.startDate === session.startDate &&
              s.endDate === session.endDate
            )
          );

          this.buildStudentList() ;
          this.applyFilters() ;
          this.loadingService.finish();
        }
      },
      error: (err: any) => {
        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-summary-session',
            startDate: startDateObj.toLocaleDateString('en-US'),
            endDate: endDateObj.toLocaleDateString('en-US'),
          }
        }) ;
      }
    })
  }

  sortTable(col: string) {
    if (this.sortColumn === col)
    {
      this.sortReverse = !this.sortReverse ;
    }
    this.sortColumn = col ;

    this.filteredSessions = (this.sortReverse) ? _.sortBy(this.filteredSessions, col).reverse() : _.sortBy(this.filteredSessions, col) ;
  }

  formatDate(dateString: string) {
    return this.utilityService.formatDate(dateString) ;
  }

  formatTime(timestamp: string) {
    return this.utilityService.formatTime(timestamp) ;
  }

  getSecondsBetweenDates(startDate: string, endDate: string) {
    let startDateObj = new Date(startDate) ;
    let endDateObj = new Date(endDate) ;
    let diff = (endDateObj.getTime() - startDateObj.getTime()) / 1000 ;
    return Math.floor(diff) ;
  }

  handleViewChange(event?: Event, value?: string): void {
    let option;
    if (event) {
      option = (event.target as HTMLSelectElement).value;
    } else if (value) {
      option = value;
    } else {
      return;
    }
    if (option === 'minutes') {
      // Total each student's time in a single pass. This used to nest a scan and a de-dupe inside a per-session loop, which
      // locked the browser up on wide date ranges (MFS, BNI, 7/22/26)
      let totalsByStudent = new Map<string, typeof this.filteredSessionsWithSeconds[0]>() ;

      for (let session of this.filteredSessions) {
        let key = sessionStudentKey(session) ;
        let totals = totalsByStudent.get(key) ;

        if (!totals) {
          totals = { ...session, seconds: 0, dateRange: { startDate: session.startDate, endDate: session.endDate } } ;
          totalsByStudent.set(key, totals) ;
        }

        totals.seconds += this.getSecondsBetweenDates(session.startDate, session.endDate) ;

        // Widen the date range to cover this session
        if (session.startDate < totals.dateRange.startDate) {
          totals.dateRange.startDate = session.startDate;
        }
        if (session.endDate > totals.dateRange.endDate) {
          totals.dateRange.endDate = session.endDate;
        }
      }

      this.filteredSessionsWithSeconds = Array.from(totalsByStudent.values()) ;
      this.viewType = "minutes";
    } else {
      this.filteredSessionsWithSeconds = [];
      this.viewType = "sessions";
    }
  }

  roundMinutes(minutes: number) {
    return Math.ceil(minutes);
  }

  printSummary() {
    let school = this.reportService.getSelectedSchoolForReports().name;
    let teacher = this.reportService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }

  exportSessionSummary() {
    let startDateObj = new Date(this.startDate.year, this.startDate.month -1, this.startDate.day, 0, 0, 1) ;
    let endDateObj = new Date(this.endDate.year, this.endDate.month - 1, this.endDate.day, 23, 59, 59) ;

    this.reportError = '' ;
    this.exportingReport = true ;
    this.reportService.downloadSessionSummary(this.reportService.getSelectedDistrictForReports().districtID,
      this.reportService.getSelectedSchoolForReports().schoolID,
      this.reportService.getSelectedTeacherForReports().userID,
      [ startDateObj.getTime(), endDateObj.getTime() ])
    .subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)
        a.href = objectUrl
        a.download = 'session_summary.xlsx';
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.exportingReport = false ;
      },
      error: (err: any) => {
        this.exportingReport = false ;
        this.reportError = `There was an error trying to export the Session Summary report. Please contact support for assistance.` ;

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-summary-session',
            action: 'export',
            districtId: this.reportService.getSelectedDistrictForReports().districtID,
            schoolId: this.reportService.getSelectedSchoolForReports().schoolID,
            teacherId: this.reportService.getSelectedTeacherForReports().userID,
            startDate: startDateObj.toLocaleDateString('en-US'),
            endDate: endDateObj.toLocaleDateString('en-US'),
          }
        }) ;
      }
    }) ;
  }
}
